import { useEffect, useState } from 'react';

/*
  How long a followed run is given to produce its document before the page concludes there is
  none. The endpoints write it before doing any work, so this only has to outlast the snapshot
  listener attaching — generous rather than tight, because being early here would call a live run
  dead, while being late only delays a message.
*/
const MISSING_RUN_GRACE_MS = 30000;

/**
 * The current time, moved forward when `deadlineMs` passes, so a component that derives "timed
 * out" from the time re-renders at the deadline instead of only when something else changes.
 *
 * The time it reports never runs behind a deadline that has fired, even if the timer fires a
 * millisecond early.
 *
 * @param {number|undefined|Function} deadline - The next deadline to wake up for in ms, or nothing
 *   to wait on. A function receives the time the caller is rendering with and returns that, for a
 *   caller whose next deadline depends on which ones have already passed.
 * @returns {number} The time in ms, as of mount or the last deadline passed.
 */
const useDeadlineNow = (deadline) => {
  const [now, setNow] = useState(() => Date.now());
  const deadlineMs = typeof deadline === 'function' ? deadline(now) : deadline;

  useEffect(() => {
    if (!deadlineMs) {
      return undefined;
    }
    const timer = setTimeout(
      () => setNow(Math.max(Date.now(), deadlineMs)),
      Math.max(deadlineMs - Date.now(), 0),
    );
    return () => clearTimeout(timer);
  }, [deadlineMs]);

  return now;
};

/**
 * Watches a server-owned run (a migrationRun or webflowDeploys document) that a page is following,
 * for the two ways such a run dies without saying so:
 *
 * - timed out: the platform killed it, so it never wrote a terminal status. `deadlineMs` is the
 *   endpoint's own timeout recorded on the run, so the page waits exactly as long as the run could
 *   still be alive.
 * - missing: the request died before the endpoint opened a document, so there is no deadline to
 *   wait on either. If the document has not appeared within MISSING_RUN_GRACE_MS it never will.
 *
 * Both are off while the run is not being watched or has finished, and reset when it stops being
 * watched, so the next run starts clean.
 *
 * @param {Object} params
 * @param {boolean} params.isWatching - Whether the page is following the run at all.
 * @param {boolean} params.isFinished - Whether the run document has a terminal status.
 * @param {boolean} params.hasDoc - Whether the run document exists.
 * @param {number} [params.deadlineMs] - The run's `deadlineAt` in ms, once known.
 * @returns {Object} { now, isTimedOut, isMissing } — `now` for callers that derive the timeout
 *   from the time themselves.
 */
const useRunWatchdog = ({
  isWatching,
  isFinished,
  hasDoc,
  deadlineMs,
}) => {
  const isOpen = isWatching && !isFinished;
  const awaitsDeadline = isOpen && !!deadlineMs;
  const awaitsDoc = isOpen && !hasDoc;

  const now = useDeadlineNow(awaitsDeadline ? deadlineMs : undefined);
  const [isGraceOver, setIsGraceOver] = useState(false);

  useEffect(() => {
    if (!awaitsDoc) {
      setIsGraceOver(false);
      return undefined;
    }
    const timer = setTimeout(() => setIsGraceOver(true), MISSING_RUN_GRACE_MS);
    return () => clearTimeout(timer);
  }, [awaitsDoc]);

  return {
    now,
    isTimedOut: awaitsDeadline && now >= deadlineMs,
    isMissing: awaitsDoc && isGraceOver,
  };
};

export default useRunWatchdog;
export {
  MISSING_RUN_GRACE_MS,
  useDeadlineNow,
};
